import { CheckIcon } from "@heroicons/react/outline";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  useUserNotificationsContext,
  useUserNotificationsHook,
} from "~/config/user/user-notifications/UserNotifications";
import { Button } from "~/design-system/Button";
import { ExtraButton } from "~/design-system/ButtonGroup";
import { MenuItem } from "~/design-system/DropdownMenu";
import { useBreakpoint } from "~/design-system/hooks";
import { createComponentHook } from "~/types";
import { MarkAllAsReadConfirmationDialog } from "./MarkAllAsReadConfirmationDialog";

const useMarkAllAsReadButton = createComponentHook(() => {
  const { t } = useTranslation();
  const { breakpoint } = useBreakpoint();
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    React.useState(false);
  const getNotifications = useUserNotificationsContext(
    (state) => state.getNotifications
  );
  const {
    actions: { handleMarkNotificationsAsRead },
  } = useUserNotificationsHook();

  const handleClearNotification = React.useCallback(() => {
    const notificationsIds = getNotifications().map((n) => n.id);
    void handleMarkNotificationsAsRead(notificationsIds);
    setOpenConfirmationDialog(false);
  }, [handleMarkNotificationsAsRead, getNotifications]);

  return {
    actions: {
      handleClearNotification,
      setOpenConfirmationDialog,
    },
    state: {
      breakpoint,
      openConfirmationDialog,
    },
    t,
  };
});

function MarkAllAsReadButton() {
  const { actions, state, t } = useMarkAllAsReadButton();
  const actionLabel = t(
    "screens.UserNotificationsScreen.UserNotificationsScreenActions.markAllAsRead",
    "Mark all as read"
  );

  if (state.breakpoint === "mobile") {
    return (
      <>
        <MarkAllAsReadConfirmationDialog
          onConfirm={actions.handleClearNotification}
          open={state.openConfirmationDialog}
          setOpen={actions.setOpenConfirmationDialog}
        />
        <ExtraButton
          variant="soft"
          color="brandAccent"
          opacity="light"
          circular={true}
          iconLabel={actionLabel}
          dataClovisActionId="notifications-actions"
        >
          <MenuItem
            icon={<CheckIcon />}
            onClick={() => actions.setOpenConfirmationDialog(true)}
            dataClovisActionId="notifications-mark-all-as-read"
          >
            {actionLabel}
          </MenuItem>
        </ExtraButton>
      </>
    );
  }

  return (
    <>
      <MarkAllAsReadConfirmationDialog
        onConfirm={actions.handleClearNotification}
        open={state.openConfirmationDialog}
        setOpen={actions.setOpenConfirmationDialog}
      />
      <Button
        iconLabel={actionLabel}
        leadingIcon={<CheckIcon />}
        variant="solid"
        tone="brandAccent"
        onClick={() => actions.setOpenConfirmationDialog(true)}
        dataClovisActionId="notifications-mark-all-as-read"
      >
        {actionLabel}
      </Button>
    </>
  );
}

export { MarkAllAsReadButton };
